import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";

// Thin wrapper over HugeiconsIcon with Dova defaults (20px, 1.8 stroke,
// currentColor). Pass any icon-data export from @hugeicons/core-free-icons.
interface IconProps {
    icon: IconSvgElement;
    size?: number;
    strokeWidth?: number;
    color?: string;
    className?: string;
}

export function Icon({ icon, size = 20, strokeWidth = 1.8, color = "currentColor", className }: IconProps) {
    return <HugeiconsIcon icon={icon} size={size} strokeWidth={strokeWidth} color={color} className={className} />;
}

// ─── Inline-SVG primitives ─────────────────────────────────────────────
// Small marks the DS draws at call-site (badges, checks, carets). All use
// currentColor so they inherit the surrounding text color.

interface SvgIconProps {
    size?: number;
    color?: string;
    className?: string;
}

export function CheckIcon({ size = 12, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none" className={className} aria-hidden>
            <path d="M2.5 6L5 8.5L9.5 3.5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Larger check for single-line success banners (24×24, M20 6L9 17l-5-5).
export function CheckLargeIcon({ size = 22, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M20 6L9 17l-5-5" />
        </svg>
    );
}

export function CaretIcon({ size = 10, color = "currentColor", className }: SvgIconProps) {
    // The stroke is divided by the scale, so it lands at 1.5 DEVICE px whatever `size` asks for
    // (QA-112). The viewBox is 10 wide, so a caret rendered at 12 scales everything up by 1.2 —
    // including the stroke, to 1.8 — and the "View more" expander was the one consumer doing that.
    // Beside a `SESSION REVIEW ›` chevron drawn 16-in-16 at a true 1.5, it read visibly heavier.
    //
    // Compensating here rather than at that call site: `strokeWidth * (size / 10)` cancels to a
    // constant 1.5, so no future size can reintroduce the defect. The three size-10 consumers
    // (Select and the pickers that use it) are unchanged — 1.5 / 1 is still 1.5.
    const strokeWidth = (1.5 * 10) / size;
    return (
        <svg width={size} height={(size * 6) / 10} viewBox="0 0 10 6" fill="none" className={className} aria-hidden>
            <path
                d="M1 1L5 5L9 1"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export function AlertTriangleIcon({ size = 22, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    );
}

export function CloseIcon({ size = 14, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 14 14"
            fill="none"
            stroke={color}
            strokeWidth="1.75"
            strokeLinecap="round"
            className={className}
            aria-hidden
        >
            <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" />
        </svg>
    );
}

export function DotIcon({ size = 6, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg width={size} height={size} viewBox="0 0 6 6" className={className} aria-hidden>
            <circle cx="3" cy="3" r="3" fill={color} />
        </svg>
    );
}

// Copy/duplicate mark — two offset rounded rectangles. Used by copy-to-clipboard
// affordances (e.g. the couple-hub re-invite card's copy-link icon).
export function CopyIcon({ size = 16, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );
}

/** Shared month-grid body for the two calendar marks below. */
function calendarFrame(color: string) {
    return (
        <>
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M16 3v4M8 3v4M3 10h18" stroke={color} />
        </>
    );
}

// Calendar — the sessions-tab zero-state's glyph (Figma 1971:3777): an empty
// calendar for "nothing is on this couple's calendar yet".
export function CalendarIcon({ size = 22, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            {calendarFrame(color)}
        </svg>
    );
}

// Calendar-plus — the standing-slot zero-state's glyph (Figma 1971:3759): the
// same frame with an add mark, for "book a recurring time".
export function CalendarPlusIcon({ size = 22, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            {calendarFrame(color)}
            <path d="M12 13v5M9.5 15.5h5" />
        </svg>
    );
}

// Ask Dova brand glyph — a chat bubble with two dots and a sparkle (Figma node
// 1348:4421). Glyph only (no background); strokes inherit currentColor, so each
// consumer supplies its own background + glyph color — the launcher FAB renders it
// white on a dark rail-color circle, the drawer empty-state renders it green. The
// sparkle is intentionally finer (1.8) than the bubble/dots (2.4), preserved from
// the source so the mark reads correctly at small sizes.
export function AskDovaIcon({ size = 28, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path
                d="M30.4693 14.0271C30.4762 13.9936 30.524 13.9936 30.5308 14.0271C30.8853 15.7614 32.2406 17.1167 33.9749 17.4713C34.0084 17.478 34.0084 17.5258 33.9749 17.5326C32.2406 17.8871 30.8853 19.2425 30.5308 20.9769C30.524 21.0103 30.4762 21.0103 30.4693 20.9769C30.1149 19.2425 28.7595 17.8871 27.0251 17.5326C26.9916 17.5258 26.9916 17.478 27.0251 17.4713C28.7595 17.1167 30.1149 15.7614 30.4693 14.0271Z"
                strokeWidth="1.8"
            />
            <path d="M23.9916 23.002H24.0006M19.9961 23.002H20.0051" strokeWidth="2.4" />
            <path
                d="M25 15.0047C24.6698 15.0016 24.3363 15 24.0001 15C22.521 15 21.0952 15.031 19.7563 15.0882C17.3157 15.1926 16.0956 15.2448 15.1302 16.2174C14.1648 17.1901 14.1229 18.3768 14.0391 20.7503C14.0136 21.4768 14 22.2292 14 23C14 23.7708 14.0136 24.5232 14.0391 25.2497C14.1229 27.6232 14.1648 28.81 15.1302 29.7826C16.0956 30.7552 17.3157 30.8074 19.7563 30.9118C19.8372 30.9152 19.9185 30.9186 20 30.922V33.2701C20 33.6732 20.3269 34 20.73 34C20.9042 34 21.0728 33.9377 21.205 33.8242L23.3846 31.9553C23.9325 31.4855 24.2065 31.2505 24.532 31.1266C24.8576 31.0026 25.2282 30.9955 25.9694 30.9815C26.7499 30.9667 27.5098 30.9432 28.2438 30.9118C30.6843 30.8074 31.9046 30.7552 32.87 29.7826C33.8354 28.81 33.8773 27.6232 33.9609 25.2497C33.9866 24.5232 34 23.7708 34 23C34 22.4912 33.9942 21.9906 33.9829 21.5"
                strokeWidth="2.4"
            />
        </svg>
    );
}

// Larger check for single-line success banners (24×24, M20 6L9 17l-5-5).

// ─── Contact / record marks ────────────────────────────────────────────
// Mail, phone, video-room, document and the row chevron. Paths are copied
// VERBATIM from the unpacked prototype rather than approximated — the Figma is
// a literal target in this repo, not a reference — and the per-icon default
// `size`/`strokeWidth` are the ones that design draws.
//
// These lived local to the Couple Settings page, on the reasoning that no other
// screen needed them yet and promoting on one page's use is premature. That
// reasoning does not survive the reuse rule (`DESIGN.md` §7): a genuinely new
// atom goes into the DS, exported from the barrel and demoed in the gallery,
// NEVER local to one surface. The rule is not about how many callers exist
// today — it is that a page-local icon set is invisible to the next person
// drawing a mail glyph, so the second one gets re-rolled slightly differently
// and the two drift. Which is exactly how a page-local set becomes a fork.

export function MailIcon({ size = 15, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2Z" />
            <path d="m22 6-10 7L2 6" />
        </svg>
    );
}

export function PhoneIcon({ size = 15, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
        </svg>
    );
}

export function VideoIcon({ size = 17, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="m22 8-6 4 6 4V8Z" />
            <rect x="2" y="6" width="14" height="12" rx="2" />
        </svg>
    );
}

export function DocumentIcon({ size = 16, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
            <path d="M14 2v4a2 2 0 0 0 2 2h4" />
            <path d="M10 9H8" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
        </svg>
    );
}

// Inherits `currentColor` like every other mark here, which is a real change from the
// page-local version: that one hard-coded `stroke="#666C66"` and therefore ignored its
// surroundings entirely. Design draws this chevron as the quiet warm grey even inside
// tinted row pills (the green "View progress note" affordance), so the two call sites
// that need it grey now say so with `text-tertiary` — the semantic utility for exactly
// that value (`--color-ink-faint: #666c66`, the same pixel the hex named).
//
// Colour belongs at the call site rather than baked in as a default, and the raw hex
// could not come into the DS regardless (`DESIGN.md` §7 — canonical tokens only). A
// `var(--color-tertiary)` default would have kept both properties in one place, but
// `var()` inside an SVG *presentation attribute* is not something this repo relies on
// anywhere else, and the failure mode is a black chevron on a page nobody re-screenshots.
export function RowChevronIcon({ size = 15, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.85"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="m9 6 6 6-6 6" />
        </svg>
    );
}

// ─── Week-calendar marks ───────────────────────────────────────────────
// The five glyphs the read-only week calendar draws that no primitive above
// already carries. Paths are the calendar prototype's own, measured by
// rendering it ("Dova Calendar.html", Shared Specs / Therapist Portal) rather
// than redrawn, so the card glyph tray and the panel's locked rows match the
// design target stroke for stroke. Each keeps the prototype's own default
// size and stroke weight — the tray marks are drawn at 11–12px and read
// heavier than a 20px icon scaled down would.
//
// NOT here, deliberately: the LOCATION PIN. The prototype draws one beside a
// street address; Notion §11.6 locks every location to Video Call for this
// build, so the only location mark is the existing `VideoIcon`. Adding a pin
// nobody may draw is how a locked field quietly unlocks.

/** Recurrence arrow — a session generated from a standing slot (`is_recurring`). */
export function RepeatIcon({ size = 11, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M4 10a8 8 0 1 1 8 8H4" />
            <path d="m8 22-4-4 4-4" />
        </svg>
    );
}

/** Two-arrow sync ring — a block that came FROM a connected calendar, not from Dova. */
export function SyncIcon({ size = 12, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M21 12a9 9 0 0 1-9 9c-2.52 0-4.93-1.06-6.64-2.9M3 12a9 9 0 0 1 9-9c2.52 0 4.93 1.06 6.64 2.9" />
            <path d="M3 16v-4h4M21 8v4h-4" />
        </svg>
    );
}

/** Closed padlock — the trailing mark on a read-only field row (`LockedField`). */
export function LockIcon({ size = 14, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
    );
}

/** Clock face — precedes a duration ("1h 15m"). */
export function ClockIcon({ size = 14, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
        </svg>
    );
}

/** Rightward arrow — the From → Until connector. Distinct from `RowChevronIcon`,
 *  which is a navigation affordance; this one is a relation between two fields. */
export function ArrowRightIcon({ size = 14, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
        </svg>
    );
}

/** Up-right arrow — a link that leaves this surface (a drill-out, an external
 *  destination). Distinct from `RowChevronIcon`, which advances within one. */
export function ArrowUpRightIcon({ size = 12, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            className={className}
            aria-hidden
        >
            <path d="M7 17 17 7M8 7h9v9" />
        </svg>
    );
}

/**
 * Warning triangle — carried on EVERY card of an overlapping pair.
 *
 * The clash is already drawn in the danger pair, but colour alone is not an
 * affordance (`DESIGN.md` §6): a therapist at the end of a long day, or one who
 * does not separate these hues, gets the tint and nothing else. The glyph is
 * the second channel, and it sits on each card rather than between them because
 * either one of them may be the one that should move.
 */
export function WarningTriangleIcon({ size = 12, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4M12 17h.01" />
        </svg>
    );
}

/** THE forward chevron, one drawing everywhere a row or card leads somewhere (ChevronAction,
 *  DrillCard, the reports list). The mirror of `CoupleHeader`'s back chevron. Replaces the
 *  "›" text glyph, whose size and weight followed whatever font it sat in. */
export function ChevronRightIcon({ size = 16, color = "currentColor", className }: SvgIconProps) {
    return (
        <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" fill="none" className={className}>
            <path
                d="M6 3.5 10.5 8 6 12.5"
                stroke={color}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
